import React from "react";
import {
	SEGMENT_CMC,
	URL_CMC_AIRCRAFT_DATA,
	URL_CMC_OPERATOR_CAPACITY_DATA,
	URL_CMC_OPERATOR_CONTACTS_DATA,
	URL_CMC_OPERATOR_SCHEDULE_DATA,
	URL_DATA_FEEDS,
} from "../constants";
import { Faq } from "../sections/faq/Faq";
import { Hero } from "../sections/hero/Hero";
import { Pricing } from "../sections/pricing/Pricing";
import { ProductSlider } from "../sections/ProductSlider";
import { ProductTabs } from "../sections/ProductTabs";
import { ProudToServe } from "../sections/proud-to-serve/ProudToServe";
import { Technology } from "../sections/Technology";
import { Testimonials } from "../../common/layout/testimonials/Testimonials";
import { Trial } from "../sections/Trial";
import { Upgrades } from "../sections/Upgrades";
import { productSegmentPageProps } from "../../common/types/product";

export default function CommercialOperatorAirportData({
	packageName,
	subscribeLink,
	tabsNav,
	testimonials,
}: productSegmentPageProps) {
	return (
		<>
			<Hero
				title={packageName}
				subtitle="14,000+ airlines and operators and airports, with real-time updates and extensive details."
				packages={[
					{
						label: SEGMENT_CMC,
						features: [
							"Global coverage: Access to 14,000+ operator and airport profiles",
						],
						subscribeLinkUrl: subscribeLink,
					},
				]}
			/>
			{tabsNav}
			<ProudToServe />
			<ProductTabs
				title={packageName}
				tabs={[
					{
						title: "Operator profile",
						description:
							"Gain a comprehensive understanding of every operator in the aviation industry with detailed, up-to-date insights:",
						list: [
							"Full IATA & ICAO code database",
							"Operators categorized by business types (scheduled carrier, cargo carrier, etc.)",
							"Status info (is the operator: active, restarting, out of business, etc.)",
							"Base insights (at which airport, country, etc. are they based)",
							"Founding year",
							"Contact information (phone number, email address, website, and links to their social media accounts)",
							"Legal name and address",
							"IOSA certification status (are they certified – yes/no)",
							"Number of countries, destinations, and routes served",
						],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/operator-profile-overview.jpg",
						},
					},
					{
						title: "Fleets/Bases",
						description:
							"Get an overview of the fleet summary for each operator:",
						list: [
							"Fleet status (how many active, inactive, wet leased, etc. aircraft do they have)",
							"Aircraft orders (how many aircraft have they ordered, by aircraft and engine type)",
							"Engine details (which engine types do they have in their fleet)",
							"Fleet history (how their fleet changed over time, etc.)",
							"Owned & leased aircraft and aircraft values (how many aircraft they own or lease, including aircraft values)",
							"Lessors (list of lessors the operator leases from)",
							"Bases (list of all airports with operator’s bases)",
						],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/operator-profile-fleets-bases.jpg",
						},
					},
					{
						title: "News",
						description:
							"Read news articles related to the specific operator:",
						list: [
							"Latest news (all news in chronological order)",
							"General news (aircraft movements, networks, commercial, operator business models, investments, stock market announcements, airport networks, aeropolitics and more)",
							"Fleet news (aircraft orders from manufacturers, eVTOL orders, new dry leases, wet leases or damp leases, second-hand aircraft purchases, tender/RFP, and more)",
							"Route network changes (route launches, route closures/suspensions, base openings, major flight cancellations and delays)",
							"Management changes (new appointments, resignations, etc.)",
						],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/operator-profile-news.jpg",
						},
					},
					{
						title: "Ownership/Partners",
						description:
							"Learn about operator's subsidiaries, alliances, codeshare agreements, and regional partnerships at a glance:",
						list: [
							"Subsidiaries (list of subsidiary operators and their IATA code & airport)",
							"Alliance info",
							"Marketing and operating codeshare agreements (list of operators they have marketing or codeshare agreements with)",
							"Regional partnerships",
						],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/operator-profile-ownership.jpg",
						},
					},
					{
						title: "Contacts",
						description:
							"Get a detailed contacts overview for almost any active as well as start-up operators worldwide:",
						list: [
							"Names & job titles",
							"Emails and LinkedIn profiles",
						],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/operator-profile-contacts.jpg",
						},
					},
					{
						title: "IT",
						description:
							"Gain insight into Passenger Service System (PSS) providers for all scheduled airlines as well as each PSS's customer list:",
						list: [
							"System type",
							"Reservation system operator is using",
							"Provider of the IT System",
						],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/operator-profile-it.jpg",
						},
					},
					{
						title: "Financials/Pax",
						description:
							"Gain key insights into an operator’s financial performance, passenger figures, and operational efficiency:",
						list: [
							"Annual financial results (insight into their revenue, profit/loss, margins, no. of employees, etc.)",
							"Annual passenger figures (insights into the number of passengers, ASK/ASM, RSK/RPM, load factor, etc.)",
						],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/operator-profile-financials.jpg",
						},
					},
					{
						title: "Capacities",
						description:
							"Look into detailed capacity, frequency, and market share information for operators and airports worldwide:",
						list: [
							"Weekly capacity, frequency, ASK and ASM",
							"Weekly Top 10 airports by capacity and frequency",
							"Weekly Top 10 routes by capacity, frequency, ASK and ASM",
						],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/operator-profile-capacities.jpg",
						},
					},
					{
						title: "Accidents and Incidents",
						description:
							"Get an overview of accidents and incidents by operator and/or aircraft variant:",
						list: [
							"Number of accidents and incidents by flight phase, flight nature, damage, occurrence, loss category so as the number of occupants & casualties",
							"List of accident and incident events including dates, aircraft details, location, etc.",
						],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/operator-profile-accidents-incidents.jpg",
						},
					},
					{
						title: "Airframe MRO",
						description:
							"Get details on more than 750 MRO Providers, which customers they serve, where their facilities are located and the type of airframe maintenance they provide (base, line + A checks, paint, etc.):",
						list: [
							"Profiles of MRO providers, along with their customer lists",
							"MRO facilities and the types of maintenance services they offer",
						],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/operator-profile-airframe-mro.jpg",
						},
					},
				]}
			/>
			<ProductSlider
				description="Search for operators using various categories and filters, including name, code, type, status, location, fleet, or engine type."
				slides={[
					{
						title: "Operator",
						description: "Search by Operator:",
						list: [
							"Name or code",
							"IOSA certification",
							"Alliance",
							"Operator type",
							"Operator status",
							"Start-up – planned start of operations",
							"Your favorite lists",
							"Reservation system",
							"ACMI lease",
						],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/operator-search-by-operator.jpg",
							title: "List of all merged scheduled carriers worldwide",
						},
					},
					{
						title: "Location",
						description: "Search for operators by location:",
						list: [
							"Homebase/airport/crew location",
							"Region",
							"Continent",
							"Country",
							"Subdivision",
							"Metro group",
						],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/operator-search-by-location.jpg",
							title: "List of all active scheduled carriers in Europe",
						},
					},
					{
						title: "Fleet",
						description: "Search for operators by fleet info: ",
						list: [
							"Aircraft market group",
							"Aircraft manufacturer",
							"Aircraft family/aircraft/aircraft type/production line/aircraft role/model/variant",
							"Number of aircraft in fleet",
							"Total seats in fleet",
							"Aircraft seat range",
						],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/operator-search-by-fleet.jpg",
							title: "List of all operators operating A220 aircraft",
						},
					},
					{
						title: "Engines",
						description: "Search for operators by:",
						list: [
							"Engine manufacturer",
							"Engine type/subtype",
							"Number of engines",
						],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/operator-search-by-engines.jpg",
							title: "List of all operators operating aircraft powered by CFM International engines",
						},
					},
				]}
			/>
			<ProductTabs
				title="Airport Data"
				tabs={[
					{
						title: "Airport Profile",
						description:
							"Get in-depth insights into airports worldwide with comprehensive data tailored for aviation professionals:",
						list: [
							"Full IATA & ICAO code database",
							"Airports categorized by business types (civil airport, airport no longer in use, etc.)",
							"Airport’s country, metro group, subdivision, coordinates and local time",
							"Elevation info",
							"Runway info (data on the longest runway)",
							"List of operator’s based and carriers operating at the airport",
							"List of aircraft stored and terminated at the airport",
							"List of aircraft types operating at the airport",
							"Today’s departures from the airport",
							"List of countries and destinations served from the airport",
							"Google Maps (airport shown on Google Maps)",
							"Airports nearby (list of airports nearby including the distance from the original airport)",
						],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/airport-profile-overview.jpg",
						},
					},
					{
						title: "News",
						description:
							"Read news articles related to the specific airport:",
						list: [
							"Latest news (all news in chronological order)",
							"General news (aircraft movements, networks, commercial, operator business models, investments, stock market announcements, airport networks, aeropolitics and more)",
							"Fleet news (aircraft orders from manufacturers, eVTOL orders, new dry leases, wet leases or damp leases, second-hand aircraft purchases, tender/RFP, and more)",
							"Route network changes (route launches, route closures/suspensions, base openings, major flight cancellations and delays)",
						],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/airport-profile-news.jpg",
						},
					},
					{
						title: "Terminals",
						description: "Overview of:",
						list: [
							"Airport terminals",
							"List of operators operating from each terminal",
						],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/airport-profile-terminals.jpg",
						},
					},
					{
						title: "Crew Bases",
						list: [
							"List of operators with crew base at the airport",
						],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/airport-profile-crews-bases.jpg",
						},
					},
					{
						title: "Capacities",
						description:
							"Look into detailed capacity, frequency, and market share information for operators and airports worldwide:",
						list: [
							"Weekly capacity, frequency, ASK and ASM",
							"Weekly Top 10 operators by capacity and frequency",
							"Weekly Top 10 routes by capacity, frequency, ASK and ASM",
						],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/airport-profile-capacities.jpg",
						},
					},
					{
						title: "Accidents and Incidents",
						description:
							"Get an overview of accidents and incidents registered at each airport:",
						list: [
							"Number of accidents and incidents by flight phase, flight nature, damage, occurrence, loss category so as the number of occupants & casualties – based on origin or destination airport and location",
							"List of accident and incident events including dates, aircraft details, location, etc. – based on origin or destination airport and location",
						],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/airport-profile-accidents-incidents.jpg",
						},
					},
					{
						title: "Airframe MRO Data",
						description:
							"Get an overview of all MRO providers and their customers at the specific airport:",
						list: [
							"List of MRO providers based at the airport",
							"List of MRO subsidiaries based at the airport",
							"Operators performing aircraft maintenance at the airport",
						],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/airport-profile-airframe-mro.jpg",
						},
					},
				]}
				inverted={true}
			/>
			<ProductSlider
				description="Search for an airport using various filters, including name, code, and airport location."
				slides={[
					{
						title: "Airport",
						description: "Search for an airport by:",
						list: ["Name or code"],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/airport-search-by-name.jpg",
							title: "Overview of Paris CDG Airport",
						},
					},
					{
						title: "Location",
						description: "Search for an airport by location:",
						list: [
							"Continent",
							"Country",
							"Subdivision",
							"Metro group",
						],
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/airport-search-by-location.jpg",
							title: "List of all airports in Germany",
						},
					},
				]}
			/>
			<ProductTabs
				title="Additional features included"
				subtitle=""
				tabs={[
					{
						title: "Start-Up Tracker",
						description:
							"Insights into any operator that is in the process of launching worldwide.",
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/start-up-tracker-2026.jpg",
							title: "Start-ups planning to start operations in 2026",
						},
					},
					{
						title: "Winners and Losers",
						description:
							"Insights into all operators that went into or out of business during a given year.",
						visual: {
							url: "/static/img/product/commercial-operator-and-airport-data/winners-and-losers-winners-2024.jpg",
							title: "All winners in 2024",
						},
					},
				]}
			/>
			<Upgrades
				packageName={packageName}
				modules={[
					{
						title: "Operator Contacts Data",
						content:
							"Get a detailed contacts overview for almost any active as well as start-up operators worldwide",
						url: URL_CMC_OPERATOR_CONTACTS_DATA,
					},
					{
						title: "Operator Schedules Data",
						content:
							"Get access to schedules of the vast majority of scheduled airlines",
						url: URL_CMC_OPERATOR_SCHEDULE_DATA,
					},
					{
						title: "Operator Capacity Data",
						content:
							"Look into detailed capacity, frequency, and market share information for airlines and airports worldwide ",
						url: URL_CMC_OPERATOR_CAPACITY_DATA,
					},
					{
						title: "Aircraft Data",
						content:
							"Get access to our extensive fleet database with over 82,000 aircraft currently being tracked",
						url: URL_CMC_AIRCRAFT_DATA,
					},
				]}
			>
				<Trial
					description="Get full, unrestricted access to all of ch-aviation’s news
					and data for 14 days. Our no-commitment guarantee:"
					subscribeLink={subscribeLink}
				/>
			</Upgrades>
			<Testimonials testimonials={testimonials} />
			<Technology />
			<Pricing
				packageName={packageName}
				description="Gain a comprehensive understanding of the aviation industry with detailed, up-to-date insights."
				downloadsDescription="Our Commercial Aviation Operator & Airport Data is ready for instant, unlimited downloads either as Excel, CSV or JSON file."
				subscribeLink={subscribeLink}
				list={[
					"Global coverage: Access to 14,000+ operator and airport profiles",
				]}
			>
				<Trial
					description="Get full, unrestricted access to all of ch-aviation’s news
					and data for 14 days. Our no-commitment guarantee:"
					subscribeLink={subscribeLink}
				/>
			</Pricing>
			<Faq
				items={[
					{
						title: "Can I download these data sets?",
						content:
							"The Commercial Aviation Operator Data includes an optional feature available for purchase that allows unlimited downloads in CSV, XLS, or JSON formats. However, the Airport Data does not include this feature but is available through our Data Feeds option. To learn more about data downloads, please contact our sales team.",
						linkUrl: "mailto:sales@ch-aviation.com",
					},
					{
						title: "Can I integrate this data as a data feed into my own system?",
						content: (
							<p>
								Yes, both Operator and Airport Data can be
								purchased as a data feed for seamless
								integration into your own system. ch-aviation
								also provides full support for the integration
								process. To learn more about data feeds, click{" "}
								<a
									href={URL_DATA_FEEDS}
									target="_blank"
									rel="noopener noreferrer"
								>
									here
								</a>
								.
							</p>
						),
					},
					{
						title: "What are the parameters for the commercial operators you track?",
						content:
							"We track all operators providing scheduled passenger or cargo flights, as well as commercial aircraft operators operating aircraft with more than 15 seats.",
					},
					{
						title: "How often is the data updated?",
						content:
							"The frequency of updates depends on the data set. Operator and Airport Data are updated on a daily, weekly, or monthly basis, ensuring you always have the latest information.",
					},
				]}
			/>
		</>
	);
}
